/**
 * DetailHeader - Unified page header for detail pages
 * 
 * Part of the Unified Detail Page Design System v1.0
 * 
 * Required Elements (left → right):
 * - Back button / Breadcrumb navigation
 * - Primary entity name (H1)
 * - Status pill (optional)
 * - Primary action buttons (right-aligned)
 * 
 * Rules:
 * - No collection-specific variations
 * - Status always visible if provided
 * - Actions must be role-aware
 * - Header is sticky on scroll
 */

import React from 'react';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import StatusPill from './StatusPill';

const DetailHeader = ({
  // Navigation
  onBack,
  backLabel = 'Back',
  breadcrumbs = [], // [{ label, onClick }]
  
  // Entity info
  entityId,
  entityName,
  entityIcon: EntityIcon,
  subtitle,
  
  // Status (optional)
  status,
  statusConfig,
  statusEditable = false,
  onStatusClick,
  
  // Refresh (optional - shows refresh button)
  onRefresh,
  refreshing = false,
  
  // Actions (right-aligned)
  primaryActions = [], // [{ label, icon, onClick, variant, testId, disabled, visible }]
  secondaryActions = [], // Same structure, rendered as outline buttons
  
  // Styling
  sticky = true,
  className = '',
  testId = 'detail-header',
}) => {
  // Filter visible actions
  const visiblePrimary = primaryActions.filter(a => a.visible !== false);
  const visibleSecondary = secondaryActions.filter(a => a.visible !== false);
  
  return (
    <div 
      className={`bg-white border-b ${sticky ? 'sticky top-0 z-10' : ''} ${className}`}
      data-testid={testId}
    >
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left Side */}
          <div className="flex items-center gap-4">
            {/* Back Button */}
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack} data-testid="detail-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {backLabel}
              </Button>
            )}
            
            {/* Breadcrumbs (alternative to back button) */}
            {breadcrumbs.length > 0 && (
              <nav className="flex items-center gap-2 text-sm">
                {breadcrumbs.map((crumb, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="text-gray-400">/</span>}
                    {crumb.onClick ? (
                      <button
                        onClick={crumb.onClick}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {crumb.label}
                      </button>
                    ) : (
                      <span className="text-gray-600">{crumb.label}</span>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            )}
            
            {/* Entity Info */}
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                {EntityIcon && <EntityIcon className="w-6 h-6" />}
                {entityName}
              </h1>
              {(entityId || subtitle) && (
                <p className="text-sm text-gray-500">
                  {entityId}
                  {entityId && subtitle && ' • '}
                  {subtitle}
                </p>
              )}
            </div>
            
            {/* Status Pill */}
            {status && statusConfig && (
              <StatusPill
                status={status}
                config={statusConfig}
                editable={statusEditable}
                onClick={onStatusClick}
                testId="header-status-pill"
              />
            )}
          </div>
          
          {/* Right Side - Actions */}
          <div className="flex items-center gap-2">
            {/* Refresh Button */}
            {onRefresh && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onRefresh}
                disabled={refreshing}
                data-testid="detail-refresh-btn"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            )}
            
            {visibleSecondary.map((action, i) => {
              const Icon = action.icon;
              return (
                <Button
                  key={i}
                  variant="outline"
                  size={action.size || 'default'}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  data-testid={action.testId}
                >
                  {Icon && <Icon className="w-4 h-4 mr-2" />}
                  {action.label}
                </Button>
              );
            })}
            
            {visiblePrimary.map((action, i) => {
              const Icon = action.icon;
              return (
                <Button
                  key={i}
                  variant={action.variant || 'default'}
                  size={action.size || 'default'}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  data-testid={action.testId}
                >
                  {Icon && <Icon className="w-4 h-4 mr-2" />}
                  {action.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailHeader;
